// @flow
import { getCookie } from '../utils/cookie';

import request, { apiURL, requestToDownload, get } from '../utils/api';

// Omits null/undefined/empty-string values so callers don't hand-build
// conditional query objects.
const buildQueryString = (params: Object): string => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    searchParams.append(key, String(value));
  });
  return searchParams.toString();
};

// Stringified up front — URLSearchParams would otherwise coerce the object
// via toString() instead of encoding it as JSON.
const serializeFilter = (filter: ?Object): string | void => {
  if (!filter || Object.keys(filter).length === 0) {
    return undefined;
  }
  return JSON.stringify(filter);
};

class KpiService {
  getKPI(
    organizationId: string,
    propertyId: string,
    kpiName: string,
    signal: Object,
  ) {
    const options: Object = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    if (signal) {
      options.signal = signal;
    }
    return request(
      `${apiURL}/${organizationId}/${propertyId}/kpi/${kpiName}`,
      options,
    );
  }

  getPortfolio(organizationId: string, requestOptions?: Object) {
    const options = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      ...(requestOptions || {}),
    };
    return request(`${apiURL}/${organizationId}/kpi/portfolio`, options);
  }

  getComplianceOverview(organizationId: string) {
    const options = {
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
    };
    return request(
      `${apiURL}/${organizationId}/kpi/compliance-overview`,
      options,
    );
  }

  getComplianceOverviewNeedsReview(organizationId: string, options?: Object) {
    return get(
      `/${organizationId}/kpi/compliance-overview/needs-review`,
      options,
    );
  }

  getComplianceOverviewOpenCerts(
    organizationId: string,
    includeNotStartedCerts?: boolean = true,
    queryParams?: {
      page?: number,
      perPage?: number,
      sortBy?: string,
      sortOrder?: string,
      filter?: Object,
      searchText?: string,
    } = {},
    options?: Object,
  ) {
    const { page, perPage, sortBy, sortOrder, filter, searchText } =
      queryParams;
    const queryString = buildQueryString({
      includeNotStartedCerts,
      page,
      perPage,
      sort_by: sortBy,
      order_by: sortOrder,
      filter: serializeFilter(filter),
      searchText,
    });
    return get(
      `/${organizationId}/kpi-helper/compliance-overview/open-certs?${queryString}`,
      options,
    );
  }

  getComplianceOverviewCounts(
    organizationId: string,
    includeNotStartedCerts?: boolean = true,
    queryParams?: { filter?: Object, searchText?: string } = {},
    options?: Object,
  ) {
    const { filter, searchText } = queryParams;
    const queryString = buildQueryString({
      includeNotStartedCerts,
      filter: serializeFilter(filter),
      searchText,
    });
    return get(
      `/${organizationId}/kpi-helper/compliance-overview/counts?${queryString}`,
      options,
    );
  }

  getComplianceOverviewFilterOptions(
    organizationId: string,
    includeNotStartedCerts?: boolean = true,
    options?: Object,
  ) {
    const queryString = buildQueryString({ includeNotStartedCerts });
    return get(
      `/${organizationId}/kpi-helper/compliance-overview/filter-options?${queryString}`,
      options,
    );
  }

  downloadKpiHomeScreenPrint(
    imageData: string,
    canvasWidth: string,
    canvasHeight: string,
  ) {
    const options = {
      method: 'POST',
      headers: {
        Authorization: getCookie('session_id'),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ canvasWidth, canvasHeight, imageData }),
    };

    return requestToDownload(
      `${apiURL}/kpi/download-home-screen-print`,
      options,
    );
  }
}

export default KpiService;
